import { useMemo } from 'react';
import { ContractsMobileFilterItem } from './ContractsMobileFilterItem';
import { FilterOption } from './ContractsMobileFilterDrawer';
import styles from './ContractsMobileFilterGroup.module.scss';

export interface SelectedItem {
  id: string;
  name: string;
}

function getSelectedIds(selectedValues: string[] | SelectedItem[]): string[] {
  if (selectedValues.length === 0) return [];
  if (typeof selectedValues[0] === 'string') {
    return selectedValues as string[];
  }
  return (selectedValues as SelectedItem[]).map((item) => item.id);
}

function getSelectedLabelsById(
  selectedValues: string[] | SelectedItem[],
): Map<string, string> {
  if (selectedValues.length === 0 || typeof selectedValues[0] === 'string') {
    return new Map();
  }
  return new Map(
    (selectedValues as SelectedItem[]).map((item) => [item.id, item.name] as const),
  );
}

interface ContractsMobileFilterGroupProps {
  label: string;
  options: FilterOption[];
  selectedValues: string[] | SelectedItem[];
  onItemClick: (optionId: string) => void;
  onItemRemove: (optionId: string) => void;
  onShowAll: () => void;
  count?: number;
}

export const ContractsMobileFilterGroup: React.FC<ContractsMobileFilterGroupProps> = ({
  label,
  options,
  selectedValues,
  onItemClick,
  onItemRemove,
  onShowAll,
  count,
}) => {
  const selectedIds = useMemo(() => getSelectedIds(selectedValues), [selectedValues]);
  const selectedLabelsById = useMemo(
    () => getSelectedLabelsById(selectedValues),
    [selectedValues],
  );
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const { visibleItems, shouldShowMore, hiddenSelectedCount } = useMemo(() => {
    const maxItemsIn2Rows = count || 2;

    const optionById = new Map(options.map((o) => [o.id, o] as const));

    const selectedOptions: FilterOption[] = selectedIds.map((id) => {
      const fromList = optionById.get(id);
      if (fromList) return fromList;
      return { id, label: selectedLabelsById.get(id) ?? id };
    });

    const selectedIdSetInner = new Set(selectedIds);
    const unselectedOptions = options.filter(
      (option) => !selectedIdSetInner.has(option.id),
    );

    if (selectedOptions.length === 0) {
      const visible = options.slice(0, maxItemsIn2Rows);

      return {
        visibleItems: visible,
        shouldShowMore: false,
        hiddenSelectedCount: 0,
      };
    }

    let visible: FilterOption[] = [];

    const selectedToShow = selectedOptions.slice(0, maxItemsIn2Rows);
    visible = [...selectedToShow];

    if (selectedOptions.length <= maxItemsIn2Rows) {
      const remainingSpace = maxItemsIn2Rows - selectedOptions.length;
      const additionalItems = unselectedOptions.slice(0, remainingSpace);
      visible = [...visible, ...additionalItems];
    }

    const hiddenSelectedCount = selectedOptions.length - selectedToShow.length;

    const shouldShowMoreButton = hiddenSelectedCount > 0;

    return {
      visibleItems: visible,
      shouldShowMore: shouldShowMoreButton,
      hiddenSelectedCount: hiddenSelectedCount,
    };
  }, [options, selectedIds, selectedLabelsById, count]);

  const hasNoOptions = options.length === 0 && selectedIds.length === 0;

  return (
    <div className={styles.filterGroup}>
      <div className={styles.label}>
        <span>{label}</span>
        {!hasNoOptions && (
          <div className={styles.filterAll} onClick={onShowAll}>
            Показать все
          </div>
        )}
      </div>

      {hasNoOptions ? (
        <div className={styles.noResults}>
          <span>Ничего не найдено</span>
        </div>
      ) : (
        <div className={styles.filterItems}>
          {visibleItems.map((option) => {
            const isSelected = selectedIdSet.has(option.id);
            return (
              <ContractsMobileFilterItem
                key={option.id}
                label={option.label}
                active={isSelected}
                onClick={() => onItemClick(option.id)}
                onRemove={isSelected ? () => onItemRemove(option.id) : undefined}
              />
            );
          })}
          {shouldShowMore && (
            <div className={styles.filterItemMore} onClick={onShowAll}>
              Еще {hiddenSelectedCount}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
